// ═══ BIT BREW — Constants & Data ═══

// Level thresholds (must match contract: loyalty_program.move)
export const LEVEL_THRESHOLDS = [0, 100, 250, 500, 1000, 2000, 5000];

/** One adventure title per level — the coral header's headline slot.
 *  Level 4 = "Crema Knight" is locked by the mockup; index is level-1. */
export const LEVEL_TITLES = [
  'Bean Sprout',      // LV.1
  'Drip Scout',       // LV.2
  'Steam Squire',     // LV.3
  'Crema Knight',     // LV.4
  'Roast Ranger',     // LV.5
  'Mocha Marshal',    // LV.6
  'Espresso Overlord' // LV.7
];

export function getLevelTitle(level: number): string {
  const idx = Math.min(Math.max(level, 1), LEVEL_TITLES.length) - 1;
  return LEVEL_TITLES[idx];
}

export function getLevelProgress(totalXP: number, level: number) {
  const nextThreshold = LEVEL_THRESHOLDS[level] || LEVEL_THRESHOLDS[LEVEL_THRESHOLDS.length - 1];
  return { totalXP, nextThreshold };
}

/** XP per purchase. Contract adds base_points, never the bonus-inclusive
 *  points_earned, on every stamping path — so xp === purchases * this. */
export const XP_PER_PURCHASE = 10;

/**
 * Every Nth coffee is free. The contract stores this per shop as a dynamic
 * field on LoyaltyProgram (`card_size`) and defaults to the same 10; this
 * mirrors the default for display. No per-shop UI exists yet, so a shop that
 * has set its own value will render a 10-slot card until that ships.
 */
export const FREE_EVERY_N = 10;

/**
 * Stamps a customer actually earns per card — one FEWER than FREE_EVERY_N.
 * The last slot is filled by the redemption, not by a purchase: nine stamps
 * fill the card and the tenth coffee is the free one.
 */
export const STAMPS_PER_CARD = FREE_EVERY_N - 1;

/**
 * Per-shop version of STAMPS_PER_CARD.
 *
 * Mirrors the contract exactly: `card_size(program, owner) - 1`. A shop that
 * has never called `set_card_size` has no dynamic field on chain and falls
 * back to DEFAULT_FREE_EVERY_N, so the 9 returned here is the SAME default,
 * not a client-side guess.
 *
 * Values below the contract's own floor (`free_every_n >= 2`) are treated as
 * unset rather than honoured — a 1 would make `purchases % n` always 0 and
 * every card look permanently empty.
 */
export function stampsPerCard(shop?: { free_every_n?: number | null } | null): number {
  const n = shop?.free_every_n;
  if (typeof n !== 'number' || n < 2) return STAMPS_PER_CARD;
  return n - 1;
}

/**
 * Stamps remaining until the next card completes, and therefore until the next
 * item box lands in the loot bag.
 *
 * This used to count to the next LEVEL threshold, because level-up was the
 * only thing that minted a RewardNFT and a card-based countdown promised
 * rewards that never came. `award_card_completion` (contract v4) makes the
 * card mechanic real, so the countdown tracks the card again — which is the
 * thing the customer is actually looking at.
 *
 * Returns 1..STAMPS_PER_CARD. Never 0: at a card boundary the reward has
 * already been minted, so the next one is a whole card away.
 */
export function stampsToNextCard(purchases: number, perCard: number = STAMPS_PER_CARD): number {
  // Guard the default rather than trusting the caller: a 0 or negative perCard
  // would make the modulo throw or return a nonsense countdown.
  const n = perCard >= 1 ? perCard : STAMPS_PER_CARD;
  return n - (purchases % n);
}

// ─── Quest System ───

export type Quest = {
  id: number;
  name: string;
  desc: string;
  target: number;
  reward: number;
  progressKey: keyof QuestProgressData;
  /**
   * Added to both progress and target for display only. Lets a quest whose
   * internal progress is shifted (see `level`) show the numbers the player
   * actually recognises. Never affects completion.
   */
  displayOffset?: number;
};

export type QuestProgressData = {
  totalPurchases: number;
  level: number;
  beans: number;
  nftCount: number;
  shopCount: number;
  questsCompleted: number;
};

export const QUESTS: Quest[] = [
  { id: 1, name: 'MORNING BREW', desc: 'Buy 5 coffees', target: 5, reward: 100, progressKey: 'totalPurchases' },
  // `level` progress is fed as `globalLevel - 1` (Dashboard.tsx), so target 4
  // fires at level 5 as the description promises. displayOffset undoes that
  // internal shift for the counter only — a Level 4 player must not be shown
  // "3 / 4" for a quest that reads "Reach Level 5".
  { id: 2, name: 'CAFFEINE RUSH', desc: 'Reach Level 5', target: 4, reward: 500, progressKey: 'level', displayOffset: 1 },
  { id: 3, name: 'BEAN HOARDER', desc: 'Collect 500 beans', target: 500, reward: 200, progressKey: 'beans' },
  { id: 4, name: 'COLLECTOR', desc: 'Earn 3 item boxes', target: 3, reward: 300, progressKey: 'nftCount' },
  { id: 5, name: 'EXPLORER', desc: 'Visit 3 different shops', target: 3, reward: 250, progressKey: 'shopCount' },
  { id: 6, name: 'COMPLETIONIST', desc: 'Complete 3 quests', target: 3, reward: 1000, progressKey: 'questsCompleted' },
];

// ─── Lore System ───

export type LoreCheckData = {
  level: number;
  totalPurchases: number;
  beans: number;
  nftCount: number;
  questsCompleted: number;
  shopCount: number;
};

export type LoreEntry = {
  id: string;
  title: string;
  text: string;
  condition: string;
  unlockCheck: (data: LoreCheckData) => boolean;
  cost?: number; // If set, this lore is purchasable with beans (legendary)
};

export const LORE_DATABASE: LoreEntry[] = [
  // Page 1 (6 cards)
  { id: 'lore_1', title: 'THE FIRST BEAN', text: 'Before graphics cards, before save files, before pause menus — there was a bean. One single pixel. One perfect roast. Everything since has been a sequel.', condition: 'Reach Level 2', unlockCheck: (d) => d.level >= 2 },
  { id: 'lore_2', title: 'ESPRESSO OVERDRIVE', text: 'Banned from speedrun competitions after a barista pulled a shot so fast it clipped through the cup and landed directly in the customer\'s bloodstream.', condition: 'Buy 3 coffees', unlockCheck: (d) => d.totalPurchases >= 3 },
  { id: 'lore_3', title: 'THE GOLDEN ERA', text: 'Back when loading a coffee took 45 minutes and you had to listen to it scream the whole time. We didn\'t complain. We didn\'t know any better. It was beautiful.', condition: 'Reach Level 3', unlockCheck: (d) => d.level >= 3 },
  { id: 'lore_4', title: 'GLITCH ROAST', text: 'The recipe called for 2 sugars. A corrupted config file made it 255. The barista couldn\'t undo it. The regulars wouldn\'t let them.', condition: 'Earn first item box', unlockCheck: (d) => d.nftCount >= 1 },
  { id: 'lore_5', title: 'PERPETUAL MOTION', text: 'After 10 cups, your character no longer walks; they vibrate through space-time.', condition: 'Buy 10 coffees', unlockCheck: (d) => d.totalPurchases >= 10 },
  { id: 'lore_6', title: 'BINARY BLEND', text: 'The ingredients list is just ones and zeros. Nobody can read it. The machine brews it anyway. It has never once been wrong.', condition: 'Reach Level 4', unlockCheck: (d) => d.level >= 4 },

  // Page 2 (6 cards)
  { id: 'lore_7', title: 'PIXEL DARK ROAST', text: 'So dark it absorbs light. Baristas must wear shaders to handle it. One sip and your screen dims automatically.', condition: 'Buy 15 coffees', unlockCheck: (d) => d.totalPurchases >= 15 },
  { id: 'lore_8', title: 'THE OVERFLOW', text: 'When you brew past the maximum cup capacity, coffee spills into adjacent memory. Side effects include answering questions nobody asked.', condition: 'Reach Level 5', unlockCheck: (d) => d.level >= 5 },
  { id: 'lore_9', title: 'RAM LATTE', text: 'Temporarily doubles your working memory but evaporates the moment you stop drinking. Contents are volatile by design.', condition: 'Buy 20 coffees', unlockCheck: (d) => d.totalPurchases >= 20 },
  { id: 'lore_10', title: 'STACK TRACE', text: 'Each layer tells a story. Espresso, milk, foam, regret, enlightenment. Follow it to the bottom and you\'ll find where it all went wrong.', condition: 'Collect 300 beans', unlockCheck: (d) => d.beans >= 300 },
  { id: 'lore_11', title: 'NULL POINTER', text: 'Points to nothing. Contains nothing. Yet somehow this cup never empties. Physicists and programmers are equally confused.', condition: 'Earn 2 item boxes', unlockCheck: (d) => d.nftCount >= 2 },
  { id: 'lore_12', title: 'BUFFER BREW', text: 'Stores up to 1024 sips before flushing directly to your bloodstream. Warning: may cause sudden bursts of productivity.', condition: 'Visit 2 shops', unlockCheck: (d) => d.shopCount >= 2 },

  // Page 3 (6 cards)
  { id: 'lore_13', title: 'KERNEL PANIC', text: 'The espresso machine froze mid-pull. The barista rebooted it. The coffee that emerged was different. Changed. Aware.', condition: 'Buy 30 coffees', unlockCheck: (d) => d.totalPurchases >= 30 },
  { id: 'lore_14', title: 'SYNTAX SUGAR', text: 'Sweetened with pure abstraction. Makes any code readable and any morning tolerable. Compilers love it. Dentists do not.', condition: 'Reach Level 6', unlockCheck: (d) => d.level >= 6 },
  { id: 'lore_15', title: 'DEADLOCK DRIP', text: 'Two baristas each waiting for the other to finish pouring. The coffee exists in a state of eternal preparation. Still waiting.', condition: 'Buy 50 coffees', unlockCheck: (d) => d.totalPurchases >= 50 },
  { id: 'lore_16', title: 'ASYNC AMERICANO', text: 'Order now, receive eventually. The cup arrives before the coffee, the steam after the sip. Time is merely a suggestion.', condition: 'Complete 1 quest', unlockCheck: (d) => d.questsCompleted >= 1 },
  { id: 'lore_17', title: 'CALLBACK CAPPUCCINO', text: 'You don\'t drink this coffee. It drinks you first, then calls you back when it\'s ready to be consumed. Patience required.', condition: 'Earn 3 item boxes', unlockCheck: (d) => d.nftCount >= 3 },
  { id: 'lore_18', title: 'MUTEX MOCHA', text: 'Only one person may sip at a time. All others must queue. Cuts through shared-state confusion with chocolate undertones.', condition: 'Complete 2 quests', unlockCheck: (d) => d.questsCompleted >= 2 },

  // Page 4 (6 cards)
  { id: 'lore_19', title: 'FORK BOMB FLAT WHITE', text: 'One cup becomes two. Two become four. By the time you look up, the entire cafe is nothing but flat whites. There is no stopping it.', condition: 'Buy 75 coffees', unlockCheck: (d) => d.totalPurchases >= 75 },
  { id: 'lore_20', title: 'SEGFAULT SPECIAL', text: 'Attempt to access forbidden grounds. The machine screams. Core dumped. What remains is a brew that shouldn\'t exist but tastes incredible.', condition: 'Reach Level 7', unlockCheck: (d) => d.level >= 7 },
  { id: 'lore_21', title: 'GARBAGE COLLECTOR', text: 'Reclaims abandoned cups from unreachable tables. Recycled into the purest brew known to compute-kind. Efficient. Relentless.', condition: 'Buy 100 coffees', unlockCheck: (d) => d.totalPurchases >= 100 },
  { id: 'lore_22', title: 'RACE CONDITION', text: 'Two orders placed simultaneously. Only one cup materializes. Which coffee wins? The answer depends on when you observe it.', condition: 'Visit 3 shops', unlockCheck: (d) => d.shopCount >= 3 },
  { id: 'lore_23', title: 'INFINITE LOOP', text: 'Sip. Refill. Sip. Refill. The barista cannot stop pouring. The customer cannot stop drinking. They say they\'re still there.', condition: 'Complete 4 quests', unlockCheck: (d) => d.questsCompleted >= 4 },
  { id: 'lore_24', title: 'THE LAST BYTE', text: 'The final entry. The archive is complete. You\'ve tasted every brew, read every log. The system acknowledges you. PLAYER ONE — LEGEND.', condition: 'Complete all quests', unlockCheck: (d) => d.questsCompleted >= 6 },

  // Page 5 — LEGENDARY (purchasable with beans)
  { id: 'lore_L1', title: 'ROOT ACCESS', text: 'They say whoever brews this blend gains admin privileges over reality itself. Handle with care.', condition: '250 beans', unlockCheck: () => false, cost: 250 },
  { id: 'lore_L2', title: 'PHANTOM PROCESS', text: 'A brew that runs in the background of your consciousness. You never see it, but it keeps everything alive.', condition: '300 beans', unlockCheck: () => false, cost: 300 },
  { id: 'lore_L3', title: 'ZERO-DAY ROAST', text: 'Discovered before anyone knew it existed. This coffee exploits a vulnerability in your sleep schedule.', condition: '350 beans', unlockCheck: () => false, cost: 350 },
  { id: 'lore_L4', title: 'QUANTUM GRIND', text: 'Both caffeinated and decaf until you take a sip. Schrödinger would have loved this blend.', condition: '400 beans', unlockCheck: () => false, cost: 400 },
  { id: 'lore_L5', title: 'EPOCH ESPRESSO', text: 'Brewed at Unix timestamp zero. Every second since has been measured in sips of this legendary shot.', condition: '450 beans', unlockCheck: () => false, cost: 450 },
  { id: 'lore_L6', title: 'SINGULARITY BLEND', text: 'The final brew. When this is consumed, all coffee becomes one. There is no going back.', condition: '500 beans', unlockCheck: () => false, cost: 500 },
];

// Pixel proverbs muttered by the mug when a tap can't brew (5% chance).
export const WISDOM_LINES = [
  'THE BEAN THAT WAITS TASTES DEEPEST.',
  'NO CUP IS EMPTY THAT HELD GOOD COFFEE.',
  'THE GRIND CONTINUES WHETHER YOU DO OR NOT.',
  'A WATCHED KETTLE STILL BOILS. EVENTUALLY.',
  'SAVE OFTEN. SIP OFTENER.',
  'EVEN LEGENDARY BEANS WERE ONCE GREEN.',
];

// Konami reward: a hidden lore card. Not in LORE_DATABASE — it renders only
// after the code fires (client-side unlock, cosmetic).
export const SECRET_LORE = {
  id: 'lore_secret_1',
  title: 'THE OLD CODE',
  text: 'Thirty lives. Two players. One bag of beans. The elders speak of a code that unlocked everything — you just proved it still works.',
  condition: 'Enter the old code',
};
